import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarDays, Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 4) {
      toast.error(t("reset_password_min"));
      return;
    }
    if (password !== passwordConfirm) {
      toast.error(t("auth_password_mismatch"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: code, new_password: password });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("reset_invalid_code"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#020617] px-4">
      <div className="w-full max-w-md relative z-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-indigo-400" />
            </div>
            <h1 className="text-2xl font-bold text-white font-heading tracking-tight">Book Flowy</h1>
          </div>
          <LanguageSwitcher variant="widget" />
        </div>

        <div className="bg-slate-950/70 backdrop-blur-xl border border-white/10 rounded-xl p-8">
          {!done ? (
            <>
              <h2 className="text-xl font-semibold text-white font-heading mb-1">{t("reset_title")}</h2>
              <p className="text-slate-400 text-sm mb-6">{t("reset_sub")}</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm">{t("reset_code_label")}</Label>
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    required
                    maxLength={6}
                    className="bg-slate-900/50 border-slate-700 focus:border-indigo-500 text-center text-2xl tracking-widest font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm">{t("reset_new_password")}</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t("reset_password_placeholder")}
                      required
                      minLength={4}
                      className="bg-slate-900/50 border-slate-700 focus:border-indigo-500 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">{t("reset_password_hint")}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm">{t("auth_password_confirm")}</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    placeholder={t("auth_password_confirm_placeholder")}
                    required
                    className={`bg-slate-900/50 border-slate-700 focus:border-indigo-500 ${passwordConfirm && password !== passwordConfirm ? "border-red-500" : ""}`}
                  />
                  {passwordConfirm && password !== passwordConfirm && (
                    <p className="text-xs text-red-400">{t("auth_password_mismatch")}</p>
                  )}
                </div>
                <Button type="submit" disabled={loading || code.length < 6} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
                  {loading ? t("loading") : t("reset_submit")}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-2">
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-500/20">
                <span className="text-2xl">✅</span>
              </div>
              <h2 className="text-lg font-bold text-white mb-2">{t("reset_done_title")}</h2>
              <p className="text-slate-400 text-sm mb-6">{t("reset_done_sub")}</p>
              <Button onClick={() => navigate("/login")} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
                {t("auth_welcome")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
